/**
 * Cart store — pure client state, persisted to localStorage.
 *
 * Cart items are a snapshot from the catalogue at add-time. The server
 * re-validates current price + stock when the buyer submits the order,
 * so a stale price in the cart isn't load-bearing: the receipt always
 * shows what the server actually charges.
 *
 * Per-user namespacing: the storage key includes the signed-in user id
 * via `setCartUser()`. On sign-out the cart is dropped from memory and
 * its localStorage row is cleared. Without this, two trade buyers on
 * the same workstation would inherit each other's carts.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { useSession } from '@/client/lib/auth'

const STORAGE_PREFIX = 'lox247_cart_v1::'
const ANON_KEY = `${STORAGE_PREFIX}anon`
let currentKey = ANON_KEY

export interface CartItem {
  productId: string
  productName: string
  unitPrice: number
  qty: number
  image?: string
  sku?: string
}

type Listener = () => void

interface CartState {
  items: CartItem[]
}

function readFromStorage(key: string): CartState {
  if (typeof window === 'undefined') return { items: [] }
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return { items: [] }
    const parsed = JSON.parse(raw) as CartState
    if (!parsed.items || !Array.isArray(parsed.items)) return { items: [] }
    return parsed
  } catch {
    return { items: [] }
  }
}

let state: CartState = readFromStorage(currentKey)
let isReady = false
const listeners = new Set<Listener>()
const readyListeners = new Set<Listener>()

function emit() {
  for (const l of listeners) l()
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(currentKey, JSON.stringify(state))
  }
}

/**
 * Bind the cart store to a user id. Call this from a useEffect when the
 * session resolves. Switching between users (sign in/out) reloads state
 * from the new user's localStorage row.
 */
export function setCartUser(userId: string | null) {
  const nextKey = userId ? `${STORAGE_PREFIX}${userId}` : ANON_KEY
  if (nextKey !== currentKey) {
    currentKey = nextKey
    state = readFromStorage(currentKey)
    for (const l of listeners) l()
  }
  if (!isReady) {
    isReady = true
    for (const l of readyListeners) l()
  }
}

/**
 * Clear cart contents AND drop the localStorage row. Call on sign-out.
 */
export function clearCartOnSignOut() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(currentKey)
  }
  state = { items: [] }
  currentKey = ANON_KEY
  for (const l of listeners) l()
  if (!isReady) {
    isReady = true
    for (const l of readyListeners) l()
  }
}

/**
 * useCartReady — true once the cart store has bound to a user (or
 * confirmed there's no signed-in user). Use this to gate "empty cart"
 * redirects on /checkout — without it you can navigate-then-redirect
 * before the user's stored cart has had a chance to load.
 */
export function useCartReady(): boolean {
  return useSyncExternalStore(
    (cb) => {
      readyListeners.add(cb)
      return () => readyListeners.delete(cb)
    },
    () => isReady,
    () => isReady,
  )
}

export const cartStore = {
  getState(): CartState {
    return state
  },
  subscribe(listener: Listener) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  addItem(item: CartItem) {
    const existing = state.items.find((i) => i.productId === item.productId)
    if (existing) {
      state = {
        items: state.items.map((i) =>
          i.productId === item.productId
            ? { ...i, qty: Math.min(i.qty + item.qty, 9999) }
            : i,
        ),
      }
    } else {
      state = { items: [...state.items, item] }
    }
    emit()
  },
  updateQty(productId: string, qty: number) {
    state = {
      items: state.items
        .map((i) => (i.productId === productId ? { ...i, qty: Math.max(0, qty) } : i))
        .filter((i) => i.qty > 0),
    }
    emit()
  },
  removeItem(productId: string) {
    state = { items: state.items.filter((i) => i.productId !== productId) }
    emit()
  },
  clear() {
    state = { items: [] }
    emit()
  },
}

/**
 * useCart — subscribe to the cart store. Re-renders when items change.
 */
export function useCart(): { items: CartItem[]; itemCount: number; subtotal: number } {
  const snapshot = useSyncExternalStore(
    cartStore.subscribe,
    cartStore.getState,
    cartStore.getState,
  )
  const itemCount = snapshot.items.reduce((s, i) => s + i.qty, 0)
  const subtotal = snapshot.items.reduce((s, i) => s + i.qty * i.unitPrice, 0)
  return { items: snapshot.items, itemCount, subtotal }
}

/**
 * Cross-tab sync — listen for storage events from other tabs and refresh
 * the in-memory state.
 */
/**
 * Bind the cart store to the currently signed-in user. Mount this once
 * in the dashboard layout — whenever the better-auth session resolves
 * to a new user id, the cart store loads from that user's namespaced
 * localStorage row. Sign-out clears state via clearCartOnSignOut().
 */
export function useBindCartToSession() {
  const { data } = useSession()
  const userId = data?.user?.id ?? null

  useEffect(() => {
    if (userId) {
      setCartUser(userId)
    } else {
      clearCartOnSignOut()
    }
  }, [userId])
}

export function useCartCrossTabSync() {
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === currentKey) {
        state = readFromStorage(currentKey)
        for (const l of listeners) l()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
}
